import React, { useEffect, useState } from "react";
import classes from './AssetLocation.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X, FileText, MapPin } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const AssetLocation = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_locations: 0,
        total_assets: 0,
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [assetLocations, setAssetLocations] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewEditModal, setShowViewEditModal] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedLocation, setSelectedLocation] = useState(null);

    // Create Modal state
    const [createFormData, setCreateFormData] = useState({
        branch_id: "",
        department_id: "",
        location_name: "",
        company_id: ""
    });
    const [submitting, setSubmitting] = useState(false);

    // Modal form data for view/edit
    const [modalData, setModalData] = useState({
        id: "",
        location_name: "",
        branch_id: "",
        department_id: "",
        company_id: ""
    });

    // Get company ID from localStorage
    useEffect(() => {
        const getCompanyId = () => {
            console.log("Attempting to get company ID from various sources...");
            
            // First check if we have a company_id directly in localStorage (set by Login component)
            const directCompanyId = localStorage.getItem("company_id");
            if (directCompanyId) {
                console.log("Found company_id in localStorage:", directCompanyId);
                setCompanyId(directCompanyId);
                setCreateFormData(prev => ({
                    ...prev,
                    company_id: directCompanyId
                }));
                return;
            }
            
            // Check for companyInfo
            const companyInfo = localStorage.getItem("companyInfo");
            if (companyInfo) {
                try {
                    const parsed = JSON.parse(companyInfo);
                    console.log("Found companyInfo:", parsed);
                    
                    // Try to get ID from various possible fields
                    const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                    if (id) {
                        setCompanyId(String(id));
                        setCreateFormData(prev => ({
                            ...prev,
                            company_id: String(id)
                        }));
                        console.log("Using company ID from companyInfo:", id);
                        return;
                    }
                } catch (error) {
                    console.error("Error parsing companyInfo:", error);
                }
            }
            
            // Check user data
            const userData = localStorage.getItem("user");
            if (userData) {
                try {
                    const parsed = JSON.parse(userData);
                    console.log("User data from localStorage:", parsed);
                    
                    // Check for direct company_id
                    if (parsed.company_id) {
                        setCompanyId(parsed.company_id);
                        setCreateFormData(prev => ({
                            ...prev,
                            company_id: parsed.company_id
                        }));
                        console.log("Using direct company_id from user:", parsed.company_id);
                        return;
                    }
                    
                    // Check for company object
                    if (parsed.company) {
                        const companyId = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                        if (companyId) {
                            setCompanyId(String(companyId));
                            setCreateFormData(prev => ({
                                ...prev,
                                company_id: String(companyId)
                            }));
                            console.log("Using company ID from user.company:", companyId);
                            return;
                        }
                    }
                    
                    // Check for companies array
                    if (parsed.companies && Array.isArray(parsed.companies) && parsed.companies.length > 0) {
                        // Try to find main company
                        const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1);
                        if (mainCompany) {
                            const companyId = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                setCreateFormData(prev => ({
                                    ...prev,
                                    company_id: String(companyId)
                                }));
                                console.log("Using main company ID from companies array:", companyId);
                                return;
                            }
                        }
                        
                        // Use first company
                        const firstCompany = parsed.companies[0];
                        if (firstCompany) {
                            const companyId = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                setCreateFormData(prev => ({
                                    ...prev,
                                    company_id: String(companyId)
                                }));
                                console.log("Using first company ID from companies array:", companyId);
                                return;
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error parsing user data:", error);
                }
            }
            
            console.warn("No company ID found in any source");
        };
        
        getCompanyId();
    }, []);

    // Fetch asset locations when companyId is available
    useEffect(() => {
        if (companyId) {
            console.log("Company ID available, fetching asset locations for company:", companyId);
            fetchAssetLocations();
        } else {
            console.warn("Company ID not available yet, waiting...");
        }
    }, [companyId]);

    const fetchAssetLocations = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                console.error("No auth token found");
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            if (!companyId) {
                console.error("Company ID is required");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please select a company and try again.'
                });
                setLoading(false);
                return;
            }

            console.log(`Fetching asset locations for company ID: ${companyId}`);
            
            const response = await axios.get(`${BASE_URL}/v1/assetlocation/fetch`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    company_id: companyId
                }
            });

            console.log("Asset locations response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                setAssetLocations(response.data.data);
                
                // Update metrics
                const totalAssets = response.data.data.reduce((sum, loc) => sum + (loc.assets_count || 0), 0);
                
                setSelectedMetrics({
                    total_locations: response.data.data.length,
                    total_assets: totalAssets
                });
                
                console.log(`Loaded ${response.data.data.length} asset locations`);
            } else {
                console.warn("Unexpected response format:", response.data);
                setAssetLocations([]);
            }
        } catch (error) {
            console.error("Error fetching asset locations:", error);
            
            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);
                
                let errorMessage = "Failed to fetch asset locations";
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 401) {
                    errorMessage = "Unauthorized. Please log in again.";
                } else if (error.response.status === 404) {
                    errorMessage = "API endpoint not found";
                }
                
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            } else if (error.request) {
                console.error("No response received:", error.request);
                Swal.fire({
                    icon: 'error',
                    title: 'Network Error',
                    text: 'No response from server. Please check your connection.'
                });
            } else {
                console.error("Error setting up request:", error.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occurred while setting up the request'
                });
            }
            
            setAssetLocations([]);
        } finally {
            setLoading(false);
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
        '/fixed-assets': 'Fixed Assets',
    };

    // Process location data for modal
    const processLocationData = (location) => {
        console.log("Processing location data:", location);
        setModalData({
            id: location.id,
            location_name: location.location_name || "",
            branch_id: location.branch_id || "",
            department_id: location.department_id || "",
            company_id: location.company_id || ""
        });
    };

    // Handle View/Edit icon clicks
    const handleViewClick = async (location) => {
        setModalMode("view");
        setSelectedLocation(location);
        setModalLoading(true);
        setShowViewEditModal(true);
        processLocationData(location);
        setModalLoading(false);
    };

    const handleEditClick = async (location) => {
        setModalMode("edit");
        setSelectedLocation(location);
        setModalLoading(true);
        setShowViewEditModal(true);
        processLocationData(location);
        setModalLoading(false);
    };

    // Handle modal input changes for view/edit
    const handleModalChange = (field, value) => {
        setModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle create form changes
    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // FIXED: Handle save for edit mode with correct /assetlocation/edit endpoint
    const handleSave = async () => {
        if (modalMode === "edit") {
            setSaveLoading(true);
            try {
                const token = localStorage.getItem("authToken");

                // Double-check company ID
                let currentCompanyId = modalData.company_id || companyId;
                
                if (!currentCompanyId) {
                    currentCompanyId = localStorage.getItem("company_id");
                }

                if (!currentCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Company ID Error',
                        text: 'Company ID not found. Please select a company and try again.'
                    });
                    setSaveLoading(false);
                    return;
                }

                // Validate location name
                if (!modalData.location_name || !modalData.location_name.trim()) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Validation Error',
                        text: 'Location name is required'
                    });
                    setSaveLoading(false);
                    return;
                }

                // Create payload with id, location_name, and company_id
                const requestData = {
                    id: selectedLocation.id,
                    location_name: modalData.location_name.trim(),
                    company_id: currentCompanyId
                };

                console.log("Updating asset location with data:", requestData);
                console.log("API Endpoint:", `${BASE_URL}/v1/assetlocation/edit`);

                // Use the correct /assetlocation/edit endpoint
                const response = await axios.post(
                    `${BASE_URL}/v1/assetlocation/edit`,
                    requestData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                console.log("Update response:", response.data);

                if (response.data.status) {
                    await fetchAssetLocations();
                    setShowViewEditModal(false);

                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: response.data.message || 'Asset location updated successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    let errorMessage = "Failed to update asset location";
                    if (response.data.message) {
                        if (typeof response.data.message === 'object') {
                            errorMessage = Object.values(response.data.message).flat().join(', ');
                        } else {
                            errorMessage = response.data.message;
                        }
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: errorMessage
                    });
                }
            } catch (error) {
                console.error("Error updating asset location:", error);

                let errorMessage = "Failed to update asset location";

                if (error.response) {
                    console.error("Error response:", error.response.data);
                    
                    if (error.response.data && error.response.data.message) {
                        if (typeof error.response.data.message === 'object') {
                            errorMessage = Object.values(error.response.data.message).flat().join(', ');
                        } else {
                            errorMessage = error.response.data.message;
                        }
                    } else if (error.response.status === 422) {
                        errorMessage = "Validation error. Please check all required fields.";
                    } else if (error.response.status === 401) {
                        errorMessage = "Unauthorized. Please log in again.";
                    } else if (error.response.status === 404) {
                        errorMessage = "API endpoint not found. Please check the URL.";
                    }
                } else if (error.request) {
                    errorMessage = "No response from server. Please check your connection.";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            } finally {
                setSaveLoading(false);
            }
        } else {
            setShowViewEditModal(false);
        }
    };

    // Handle create submit - WITHOUT department_id and branch_id
    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.location_name || !createFormData.location_name.trim()) {
            Swal.fire("Error", "Location name is required", "error");
            return;
        }

        // Double-check company ID
        let currentCompanyId = companyId;
        
        if (!currentCompanyId) {
            currentCompanyId = localStorage.getItem("company_id");
            
            if (!currentCompanyId) {
                const companyInfo = localStorage.getItem("companyInfo");
                if (companyInfo) {
                    try {
                        const parsed = JSON.parse(companyInfo);
                        currentCompanyId = parsed?.id || parsed?.company_id || parsed?.companyId;
                    } catch (error) {
                        console.error("Error parsing companyInfo:", error);
                    }
                }
            }
            
            if (currentCompanyId) {
                setCompanyId(currentCompanyId);
            }
        }

        if (!currentCompanyId) {
            Swal.fire({
                icon: 'error',
                title: 'Company ID Error',
                text: 'Company ID not found. Please select a company and try again.',
                confirmButtonText: 'Go to Dashboard'
            }).then(() => {
                navigate('/dashboard');
            });
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const payload = {
                location_name: createFormData.location_name.trim(),
                company_id: currentCompanyId
            };

            console.log("Creating asset location with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/v1/assetlocation/create`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            console.log("Create response:", response.data);

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Asset location created successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });
                
                resetCreateForm();
                onClose();
                await fetchAssetLocations();
            } else {
                Swal.fire("Error", response.data?.message || "Failed to create asset location", "error");
            }
        } catch (error) {
            console.error("Error creating asset location:", error);
            
            let errorMessage = "Something went wrong while creating the location";
            if (error.response?.data?.message) {
                if (typeof error.response.data.message === 'object') {
                    errorMessage = Object.values(error.response.data.message).flat().join(', ');
                } else {
                    errorMessage = error.response.data.message;
                }
            }
            
            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = async (location) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                console.log(`Deleting asset location with ID: ${location.id}`);
                
                const response = await axios.delete(
                    `${BASE_URL}/v1/assetlocation/${location.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                console.log("Delete response:", response.data);

                if (response.data.status) {
                    await fetchAssetLocations();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Asset location deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || "Failed to delete asset location");
                }
            } catch (error) {
                console.error("Error deleting asset location:", error);

                let errorMessage = "Failed to delete asset location";
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    const resetCreateForm = () => {
        setCreateFormData({
            branch_id: "",
            department_id: "",
            location_name: "",
            company_id: companyId
        });
    };

    const onClose = () => {
        setShowCreateModal(false);
        resetCreateForm();
    };

    // Skeleton loader for table rows
    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    // Skeleton for modal form inputs
    const ModalInputSkeleton = () => (
        <div className={classes.skeletonInput}></div>
    );

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname] || "Fixed Assets"} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns} onClick={() => navigate('/transactions/sales-invoice/sale')}>
                            Sales Invoice
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/Inventory/PurchaseOrder')}>
                            Purchase Order
                        </button>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Stock Delivery
                        </button>
                        <button onClick={() => navigate('/settings/product-services')} className={`${classes.btns} ${classes.greens}`}>
                            Product & Services
                        </button>
                    </div>
                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Asset Locations</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Locations</p>
                                    <p className={classes.kdptg1}>
                                        {selectedMetrics?.total_locations?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Active locations</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Assets</p>
                                    <p className={classes.kdptg2}>
                                        {selectedMetrics?.total_assets?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Across all locations</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search locations" />
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>

                        <button className={classes.btnss} onClick={() => setShowCreateModal(true)}>
                            + Add Asset Location
                        </button>
                    </div>

                    <table className={classes.invoiceTable}>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Location Name</th>
                                <th>Branch</th>
                                <th>Department</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton />
                            ) : assetLocations.length > 0 ? (
                                assetLocations.map((location, index) => (
                                    <tr key={location.id}>
                                        <td>{index + 1}</td>
                                        <td>{location.location_name || "—"}</td>
                                        <td>{location.branch_id || "—"}</td>
                                        <td>{location.department_id || "—"}</td>
                                        <td>
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                <Eye
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#007bff" }}
                                                    onClick={() => handleViewClick(location)}
                                                />
                                                <Pencil
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#28a745" }}
                                                    onClick={() => handleEditClick(location)}
                                                />
                                                <Trash2
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#dc3545" }}
                                                    onClick={() => handleDeleteClick(location)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                                        {loading ? "Loading..." : "No asset locations found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View/Edit Modal */}
            {showViewEditModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <MapPin className={classes.icon} />
                                <h2>{modalMode === "edit" ? "Edit Asset Location" : "View Asset Location"}</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewEditModal(false)} disabled={saveLoading}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.formGrid}>
                            {modalLoading ? (
                                Array.from({ length: 4 }).map((_, index) => (
                                    <div className={classes.formGroup} key={index}>
                                        <div className={classes.skeletonLabel}></div>
                                        <ModalInputSkeleton />
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className={classes.formGroup}>
                                        <label>Location Name {modalMode === "edit" && "*"}</label>
                                        <input
                                            type="text"
                                            value={modalData.location_name}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("location_name", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2}
                                            placeholder="Enter location name"
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Branch</label>
                                        <input
                                            type="text"
                                            value={modalData.branch_id}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("branch_id", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2}
                                            placeholder="Branch (for display only)"
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Department</label>
                                        <input
                                            type="text"
                                            value={modalData.department_id}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("department_id", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2}
                                            placeholder="Department (for display only)"
                                        />
                                    </div>

                                    <input type="hidden" value={modalData.company_id} />
                                </>
                            )}
                        </div>

                        <button
                            className={classes.saveBtn}
                            onClick={handleSave}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <span className={classes.loadingText}>
                                    <span className={classes.spinner}></span>
                                    Saving...
                                </span>
                            ) : (
                                modalMode === "edit" ? "Save Changes" : "Close"
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <MapPin className={classes.icon} />
                                <h2>Add Asset Location</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={onClose}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit}>
                            <div className={classes.formGrid}>
                                <div className={classes.formGroup}>
                                    <label>Location Name *</label>
                                    <input
                                        type="text"
                                        name="location_name"
                                        placeholder="e.g., Main Office, Warehouse A, Storage Room"
                                        value={createFormData.location_name}
                                        onChange={handleCreateChange}
                                        required
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Branch</label>
                                    <input
                                        type="text"
                                        name="branch_id"
                                        placeholder="Enter branch (for display only)"
                                        value={createFormData.branch_id}
                                        onChange={handleCreateChange}
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Department</label>
                                    <input
                                        type="text"
                                        name="department_id"
                                        placeholder="Enter department (for display only)"
                                        value={createFormData.department_id}
                                        onChange={handleCreateChange}
                                    />
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                className={classes.saveBtn} 
                                disabled={submitting}
                            >
                                {submitting ? "Creating..." : "Add Asset Location"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AssetLocation;